import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { RootState } from "../../app/store";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { PagerTargetEvent } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { useLocation } from "react-router-dom";
import {
  deleteFBLead,
  getAllStudentLeads,
} from "./services/integration.services";
import {
  clearFBLeadID,
  clearStudentLeadList,
  setFBLeadID,
} from "./integrationSlice";
import { LoadingPanel } from "../../components/layout/Loading";
import AlertBox from "../../components/common/AlertBox";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { MdDelete } from "react-icons/md";
import IconButton from "../../components/common/IconButton";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { Button } from "@progress/kendo-react-buttons";
import { checkAcessRights } from "../../_helper/helper";
import { EXCEL_EXPORT_ACCESS } from "../../_contstants/common";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import moment from "moment";

const LeadCenterList: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const FormID = location?.state?.FormID;
  const isExcelExportAccess = checkAcessRights(
    "/integrations",
    EXCEL_EXPORT_ACCESS,
  );
  const _export = React.useRef<ExcelExport | null>(null);
  const excelExport = () => {
    if (_export.current !== null) {
      _export.current.save();
    }
  };

  useEffect(() => {
    if (FormID) {
      dispatch(getAllStudentLeads(FormID));
    }

    return () => {
      dispatch(clearStudentLeadList());
    };
  }, []);

  return (
    <GridLayout
      cols={[{ width: "100%" }]}
      style={{ height: "100%", margin: "4px 10px" }}
    >
      <GridLayoutItem
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "15px 0px 15px 0",
          height: "80px",
        }}
      >
        <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
          FB/Insta Lead List
        </Typography.h4>
        {isExcelExportAccess && (
          <Button
            type="button"
            fillMode={"solid"}
            themeColor={"primary"}
            onClick={excelExport}
          >
            Excel Export
          </Button>
        )}
      </GridLayoutItem>
      <GridLayoutItem>
        <LeadGridComponent ref={_export} />
      </GridLayoutItem>
    </GridLayout>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const LeadGridComponent = ({ ref }: { ref: any }) => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const StudentLeadList = useAppSelector(
    (state: RootState) => state.integration.StudentLeadList,
  );
  const loading = useAppSelector(
    (state: RootState) => state.integration.loading,
  );

  const [uiLoading, setLoading] = useState<boolean>(false);
  const [isUpdated, setUpdated] = useState<boolean>(false);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();
  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? StudentLeadList?.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  useEffect(() => {
    setUpdated(!isUpdated);
  }, [JSON.stringify(StudentLeadList), StudentLeadList.length]);

  useEffect(() => {
    setLoading(loading);
  }, [loading]);

  if (uiLoading) return <LoadingPanel gridRef={gridRef} />;

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 1 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const MyEditCommandCell = (props: any) => {
    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteFBLead"));
      dispatch(setFBLeadID(ID));
    };

    return (
      <td
        {...props.tdProps}
        className={`action-td ${props.className}`}
        style={{ ...props.tdProps.style }}
      >
        <IconButton
          type="button"
          fillMode="flat"
          size="small"
          title="Delete"
          className="color-red"
          onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
          icon={MdDelete}
          iconClassName="absolute-position"
          iconStyle={{ fontSize: "24px" }}
        />
      </td>
    );
  };

  return (
    <>
      <DeleteFBLeadDialog />
      {StudentLeadList && StudentLeadList?.length > 0 ? (
        <ExcelExport
          data={StudentLeadList}
          ref={ref}
          fileName={`FB_Insta_Lead_list_${moment().format("DD-MM-YYYY hh-mm A")}.xlsx`}
        >
          <KendoGrid
            style={{ maxHeight: "calc(100vh - 175px)" }}
            filterable={true}
            filter={filter}
            data={
              filter
                ? filterBy(StudentLeadList, filter)?.slice(
                    page.skip,
                    page.take + page.skip,
                  )
                : StudentLeadList?.slice(page.skip, page.take + page.skip)
            }
            onFilterChange={handleFilterChange}
            skip={page.skip}
            take={page.take}
            total={StudentLeadList?.length}
            pageable={{
              buttonCount: 5,
              pageSizes: [5, 10, 15, "All"],
              pageSizeValue: pageSizeValue,
              type: "input",
            }}
            onPageChange={pageChange}
            cells={{
              headerCell: HeaderCustomCell,
              data: MyDataCustomCell,
            }}
          >
            <Column field="FullName" title="Name" />
            <Column field="MobileNo" title="Mobile No." />
            <Column field="EmailID" title="Email ID" />
            <Column
              field=""
              title="Actions"
              cells={{ data: MyEditCommandCell }}
              width={"85px"}
              filterable={false}
              locked={true}
            />
          </KendoGrid>
        </ExcelExport>
      ) : (
        <AlertBox />
      )}
    </>
  );
};

const DeleteFBLeadDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const FBLeadID = useAppSelector((state) => state.integration.FBLeadID);
  const location = useLocation();
  const FormID = location?.state?.FormID;

  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteFBLead(ID));
      if (response.meta.requestStatus === "fulfilled" && FormID) {
        dispatch(closeDialog());
        dispatch(clearFBLeadID());
        dispatch(getAllStudentLeads(FormID));
      }
    } else {
      if (FormID) {
        dispatch(closeDialog());
        dispatch(clearFBLeadID());
        dispatch(getAllStudentLeads(FormID));
      }
    }
  };
  return (
    <>
      {dialogName === "deleteFBLead" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete FB/Insta Lead?"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Lead?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(FBLeadID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default LeadCenterList;
